package no.vareflow.app.report

import no.vareflow.app.domain.AppLanguage
import no.vareflow.app.domain.t
import no.vareflow.app.domain.tr

/** How the counted lines are grouped in a report. Labels are in the UI language (the dialog). */
enum class ReportGrouping(private val nb: String, private val en: String) {
    CATEGORY("Hovedvaregruppe", "Main category"),
    SUPPLIER("Leverandør", "Supplier"),
    BOTH("Hovedvaregruppe og leverandør", "Main category and supplier");

    val label: String get() = t(nb, en)
}

/** One sheet / continuous document, or one sheet (PDF: new page) per group plus a summary sheet. */
enum class ReportLayout(private val nb: String, private val en: String, private val hintNb: String, private val hintEn: String) {
    SINGLE("Alt på ett ark", "All on one sheet", "Oppsummeringen kommer til slutt", "The summary comes at the end"),
    PER_GROUP("Ett ark per gruppe", "One sheet per group", "Pluss et eget ark med oppsummering", "Plus a separate summary sheet");

    val label: String get() = t(nb, en)
    val hint: String get() = t(hintNb, hintEn)
}

enum class ReportFormat(val label: String, val extension: String, val mimeType: String) {
    XLSX("Excel", "xlsx", "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"),
    PDF("PDF", "pdf", "application/pdf")
}

/** One counted product (all departments added together). */
data class ReportLine(
    val varenummer: String,
    val name: String,
    val category: String,
    val supplier: String,
    val unit: String,
    val quantity: Double,
    val pricePerUnit: Double,
    /** Undergruppe inside the hovedvare (e.g. Rødvin); blank = none. */
    val undergruppe: String = ""
) {
    val value: Double get() = quantity * pricePerUnit
}

data class ReportRequest(
    /** e.g. "Varetelling Oktober 2026". */
    val title: String,
    /** e.g. "Hønefoss · Aktiv · laget 08.10.2026 23:40 av Vareflow Admin". */
    val subtitle: String,
    val lines: List<ReportLine>,
    val grouping: ReportGrouping,
    val layout: ReportLayout,
    /** Without the price permission the report has no price or value columns. */
    val showPrices: Boolean,
    /** Hovedvaregrupper in the app's order; unknown ones are sorted after these. */
    val categoryOrder: List<String> = emptyList(),
    /** Undergrupper in the app's order (as on the Telling page); unknown ones alphabetically after. */
    val undergruppeOrder: List<String> = emptyList(),
    /** Language of the report's own texts (column titles, sums, page numbers); chosen in Lag rapport. */
    val language: AppLanguage = AppLanguage.NB,
    /** Company name for the report header (blank: none). */
    val companyName: String = "",
    /** Company logo as JPEG on white (CompanyLogo.jpeg), drawn in the header; null: none. */
    val logoJpeg: ByteArray? = null,
    /** Registered svinn of the month: its own sheet (PDF: own pages); empty = no svinn sheet. */
    val svinn: List<no.vareflow.app.domain.SvinnRecord> = emptyList()
)

/** Group markers for lines without leverandør / Hovedvaregruppe; shown via [reportGroupName]. */
const val NO_SUPPLIER = "Uten leverandør"
const val UNKNOWN_CATEGORY = "Ukjent"

/** A group name as written in the report: the two markers above are translated, real names are kept. */
fun reportGroupName(name: String, language: AppLanguage): String = when (name) {
    NO_SUPPLIER -> tr(language, "Uten leverandør", "No supplier")
    UNKNOWN_CATEGORY -> tr(language, "Ukjent", "Unknown")
    else -> name
}

// ---------------------------------------------------------------- the laid-out report, ready for a writer

enum class CellKind { TEXT, QUANTITY, MONEY, COUNT }

data class ReportColumn(
    val title: String,
    val kind: CellKind,
    /** Relative width (also ≈ Excel characters). */ val width: Int,
    /** Language-independent id of the column (title is translated). */ val key: String = title
)

sealed class ReportRow {
    /** Big heading for a group (Hovedvaregruppe or leverandør). */
    data class Heading(val text: String) : ReportRow()
    /** Smaller heading inside a group (leverandør inside a Hovedvaregruppe). */
    data class SubHeading(val text: String) : ReportRow()
    /** Column titles. */
    data class Header(val columns: List<ReportColumn>) : ReportRow()
    /** A data row; numbers are kept as numbers so Excel can calculate with them. */
    data class Item(val cells: List<Any>) : ReportRow()
    /** Sum row of a (sub)group or the whole report; [cells] line up with the columns, null = empty. */
    data class Sum(val cells: List<Any?>, val strong: Boolean) : ReportRow()
    object Blank : ReportRow()
}

/** One Excel sheet, or one run of pages in the PDF (a new page starts for every sheet). */
data class ReportSheet(val name: String, val columns: List<ReportColumn>, val rows: List<ReportRow>)

data class LaidOutReport(
    val title: String,
    val subtitle: String,
    val sheets: List<ReportSheet>,
    val language: AppLanguage = AppLanguage.NB,
    val companyName: String = "",
    /** Only a JPEG that [JpegInfo] can read is kept. */
    val logo: JpegInfo? = null
)

/** Size and colour layout of a JPEG, read from its header (for embedding in PDF / Excel). */
class JpegInfo(val bytes: ByteArray, val width: Int, val height: Int, val components: Int) {
    companion object {
        /** null when [bytes] is not a JPEG with a readable frame header. */
        fun read(bytes: ByteArray?): JpegInfo? {
            if (bytes == null || bytes.size < 4 || (bytes[0].toInt() and 0xFF) != 0xFF || (bytes[1].toInt() and 0xFF) != 0xD8) return null
            var i = 2
            fun u8(p: Int) = bytes[p].toInt() and 0xFF
            while (i + 3 < bytes.size) {
                if (u8(i) != 0xFF) { i++; continue }
                val marker = u8(i + 1)
                if (marker == 0xFF) { i++; continue }
                if (marker == 0xD8 || marker == 0x01 || marker in 0xD0..0xD7) { i += 2; continue }
                if (marker == 0xD9 || marker == 0xDA) return null
                val length = (u8(i + 2) shl 8) or u8(i + 3)
                val isFrame = marker in 0xC0..0xCF && marker != 0xC4 && marker != 0xC8 && marker != 0xCC
                if (isFrame && i + 9 < bytes.size) {
                    val height = (u8(i + 5) shl 8) or u8(i + 6)
                    val width = (u8(i + 7) shl 8) or u8(i + 8)
                    val components = u8(i + 9)
                    return if (width > 0 && height > 0 && (components == 1 || components == 3)) JpegInfo(bytes, width, height, components) else null
                }
                i += 2 + length
            }
            return null
        }
    }
}

/** Builds and writes a report in the chosen format. */
object Reports {
    fun create(request: ReportRequest, format: ReportFormat): ByteArray {
        val laidOut = ReportBuilder.build(request)
        return when (format) {
            ReportFormat.XLSX -> XlsxWriter.write(laidOut)
            ReportFormat.PDF -> PdfWriter.write(laidOut)
        }
    }
}
